import { useState } from "react";
import {
  Sparkles,
  RefreshCw,
  X,
  Edit3,
  ArrowRight,
  Loader2,
  ListChecks,
  Wand2,
  PenLine,
} from "lucide-react";
import { adminSuggestTopic, adminGenerateArticle } from "@/lib/blogApi";

/**
 * Modal stages:
 *  - "choose":   pick AI-suggest path OR write-your-own path
 *  - "manual":   admin types their own topic
 *  - "suggesting": AI is ideating a topic (only on the AI path)
 *  - "reviewing":  AI suggestion shown for review/customize/regenerate
 *  - "writing":    AI is writing the full article
 */
export default function TopicSuggestionModal({ open, onClose, onComplete }) {
  const [stage, setStage] = useState("choose");
  const [topic, setTopic] = useState(null);
  const [error, setError] = useState("");
  const [customizing, setCustomizing] = useState(false);

  // Editable fields (used in reviewing-customize stage)
  const [editTitle, setEditTitle] = useState("");
  const [editQuestion, setEditQuestion] = useState("");
  const [editOutline, setEditOutline] = useState("");

  // Manual stage: just a single topic the user types
  const [seedTopic, setSeedTopic] = useState("");

  const reset = () => {
    setStage("choose");
    setTopic(null);
    setError("");
    setCustomizing(false);
    setEditTitle("");
    setEditQuestion("");
    setEditOutline("");
    setSeedTopic("");
  };

  const handleClose = () => {
    if (stage === "writing") return; // don't allow closing mid-write
    reset();
    onClose?.();
  };

  // AI path
  const suggest = async () => {
    setStage("suggesting");
    setError("");
    try {
      const t = await adminSuggestTopic();
      setTopic(t);
      setEditTitle(t.title || "");
      setEditQuestion(t.target_question || "");
      setEditOutline((t.outline || []).join("\n"));
      setStage("reviewing");
    } catch (e) {
      setError(e.message || "Failed to suggest a topic. Try again.");
      setStage("choose");
    }
  };

  // Manual path
  const startManual = () => {
    setStage("manual");
    setSeedTopic("");
    setError("");
  };

  const acceptAiTopic = () => writeFromTopic({ ...topic, _baseTopic: topic });

  const submitManual = async () => {
    const seed = seedTopic.trim();
    if (!seed) {
      setError("Type the topic you want to write about.");
      return;
    }
    setStage("writing");
    setError("");
    try {
      const article = await adminGenerateArticle({ seed });
      reset();
      onComplete?.(article);
    } catch (e) {
      setError(e.message || "Article generation failed.");
      setStage("manual");
    }
  };

  const writeFromTopic = async (topicToWrite) => {
    setStage("writing");
    setError("");
    // For AI path with possible customization
    const finalTopic = {
      ...topicToWrite,
      title: (editTitle || topicToWrite.title || "").trim(),
      target_question: (editQuestion || topicToWrite.target_question || "").trim(),
      outline: (
        editOutline
          ? editOutline
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : topicToWrite.outline || []
      ),
    };
    try {
      const article = await adminGenerateArticle(finalTopic);
      reset();
      onComplete?.(article);
    } catch (e) {
      setError(e.message || "Article generation failed.");
      setStage(topic ? "reviewing" : "manual");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="topic-suggest-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#D41F30] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <h2 className="font-display text-xl font-bold text-gray-900">
              {stage === "writing"
                ? "Writing your article..."
                : stage === "manual"
                  ? "Your topic"
                  : "Generate new article"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            data-testid="modal-close"
            disabled={stage === "writing"}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-30"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {error && (
            <div
              className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"
              data-testid="topic-modal-error"
            >
              {error}
            </div>
          )}

          {/* CHOOSE STAGE */}
          {stage === "choose" && (
            <div className="space-y-4" data-testid="topic-choose">
              <p className="text-gray-600 leading-relaxed">
                How would you like to start? You can give the AI a specific topic, or let it pick one for you.
              </p>
              <button
                onClick={startManual}
                data-testid="choose-manual"
                className="w-full text-left p-5 border-2 border-gray-200 hover:border-[#D41F30] rounded-2xl transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-50 group-hover:bg-[#D41F30] flex items-center justify-center transition-colors flex-shrink-0">
                    <PenLine className="w-5 h-5 text-[#D41F30] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-gray-900 mb-1">
                      Write about my own topic
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      You give the AI the title and the question to answer. Optional outline. The AI handles
                      the rest in your CityWorth brand voice.
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={suggest}
                data-testid="choose-ai"
                className="w-full text-left p-5 border-2 border-gray-200 hover:border-[#D41F30] rounded-2xl transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-50 group-hover:bg-[#D41F30] flex items-center justify-center transition-colors flex-shrink-0">
                    <Wand2 className="w-5 h-5 text-[#D41F30] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-gray-900 mb-1">
                      Let AI suggest a topic
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      AI picks a trending homebuyer or mortgage question. You can regenerate or customize
                      before it writes the article.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* MANUAL STAGE */}
          {stage === "manual" && (
            <div className="space-y-4" data-testid="topic-manual">
              <div>
                <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mb-2">
                  What do you want this article to be about?
                </label>
                <textarea
                  value={seedTopic}
                  onChange={(e) => setSeedTopic(e.target.value)}
                  placeholder='e.g. "How to remove PMI from a conventional loan" or "Using a VA loan to buy a second home"'
                  rows={4}
                  data-testid="manual-seed"
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30 focus:border-[#D41F30]"
                  autoFocus
                />
                <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                  Just type the topic. The AI fills in the title, meta description, keywords, outline, category, and writes the full article in your CityWorth brand voice.
                </p>
              </div>
            </div>
          )}

          {/* SUGGESTING STAGE */}
          {stage === "suggesting" && (
            <div className="py-12 text-center" data-testid="topic-suggesting">
              <Loader2 className="w-8 h-8 mx-auto text-[#D41F30] animate-spin mb-3" />
              <p className="text-sm text-gray-600">AI is picking a fresh, on-brand topic...</p>
            </div>
          )}

          {/* WRITING STAGE */}
          {stage === "writing" && (
            <div className="py-12 text-center" data-testid="topic-writing">
              <Loader2 className="w-8 h-8 mx-auto text-[#D41F30] animate-spin mb-3" />
              <p className="text-sm text-gray-600">
                Writing the full article (this takes about 60 seconds)...
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Generating featured image with Nano Banana...
              </p>
            </div>
          )}

          {/* REVIEWING STAGE */}
          {stage === "reviewing" && topic && (
            <div className="space-y-5">
              {!customizing ? (
                <>
                  <div>
                    <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-2">
                      Proposed title
                    </div>
                    <h3 className="font-display text-2xl font-black text-gray-900 leading-tight">
                      {topic.title}
                    </h3>
                  </div>

                  <div>
                    <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-1">
                      Question this answers
                    </div>
                    <p className="text-gray-700 italic">&ldquo;{topic.target_question}&rdquo;</p>
                  </div>

                  {topic.meta_description && (
                    <div>
                      <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-1">
                        Meta description (for Google)
                      </div>
                      <p className="text-sm text-gray-600">{topic.meta_description}</p>
                    </div>
                  )}

                  {topic.outline && topic.outline.length > 0 && (
                    <div>
                      <div className="text-xs font-bold tracking-wider uppercase text-gray-500 mb-2 flex items-center gap-1">
                        <ListChecks className="w-3 h-3" /> Proposed outline
                      </div>
                      <ul className="space-y-1.5">
                        {topic.outline.map((h, i) => (
                          <li
                            key={i}
                            className="text-sm text-gray-700 flex items-start gap-2"
                          >
                            <span className="inline-block w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                              {i + 1}
                            </span>
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2.5 py-1 rounded-full bg-red-50 text-[#D41F30] font-semibold">
                      {topic.category}
                    </span>
                    {(topic.secondary_keywords || []).slice(0, 4).map((k) => (
                      <span key={k} className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                        {k}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-4" data-testid="topic-customize">
                  <div>
                    <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mb-1.5">
                      Title
                    </label>
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30"
                      data-testid="topic-edit-title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mb-1.5">
                      Target question
                    </label>
                    <input
                      value={editQuestion}
                      onChange={(e) => setEditQuestion(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30"
                      data-testid="topic-edit-question"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-wider uppercase text-gray-500 mb-1.5">
                      Outline (one H2 per line)
                    </label>
                    <textarea
                      value={editOutline}
                      onChange={(e) => setEditOutline(e.target.value)}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#D41F30]/30"
                      data-testid="topic-edit-outline"
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Heads up: category, keywords, and SEO meta will use the AI&apos;s original suggestion.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {stage === "manual" && (
          <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:justify-end sticky bottom-0 bg-white">
            <button
              onClick={() => setStage("choose")}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={submitManual}
              disabled={!seedTopic.trim()}
              data-testid="manual-submit"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-full bg-[#D41F30] hover:bg-[#B91C2A] disabled:opacity-50 disabled:cursor-not-allowed text-white shadow"
            >
              Write this article
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {stage === "reviewing" && (
          <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:justify-end sticky bottom-0 bg-white">
            <button
              onClick={suggest}
              data-testid="topic-regenerate"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Suggest another
            </button>
            <button
              onClick={() => setCustomizing((c) => !c)}
              data-testid="topic-customize-toggle"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Edit3 className="w-4 h-4" />
              {customizing ? "Done editing" : "Customize"}
            </button>
            <button
              onClick={acceptAiTopic}
              data-testid="topic-accept"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-full bg-[#D41F30] hover:bg-[#B91C2A] text-white shadow"
            >
              Write this article
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
